import { FC, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  Download,
  InsertDriveFile,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";

import {
  useGetIeepaAnalysisReportQuery,
  useRegenerateIeepaAnalysisWorkbookMutation,
} from "@/services";
import { tokens } from "@/tokens";
import { EntryTable } from "./EntryTable";
import { FunnelOverview } from "./FunnelOverview";
import { useCapeAnalytics } from "./useCapeAnalytics";
import {
  ExportCapeReportDialog,
  ExportOptions,
} from "./ExportCapeReportDialog";
import type { IeepaAnalysisEntry } from "@/types/ieepa";
import { buildCapeReportCsv } from "./capeReportCsv";

const fmtDate = (v: string | null): string => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime())
    ? "—"
    : `${String(d.getMonth() + 1).padStart(2, "0")}/${String(
        d.getDate()
      ).padStart(2, "0")}/${d.getFullYear()}`;
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

interface IeepaRefundCapeDetailProps {
  reportId?: string;
  hideBackButton?: boolean;
}

const IeepaRefundCapeDetail: FC<IeepaRefundCapeDetailProps> = ({
  reportId: reportIdProp,
  hideBackButton,
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const reportId = reportIdProp ?? params.reportId ?? "";
  const showBackButton = !hideBackButton && reportIdProp === undefined;

  const { data: report, isLoading, error } = useGetIeepaAnalysisReportQuery(
    { reportId },
    { skip: !reportId, refetchOnMountOrArgChange: true }
  );

  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "certified"
    | "pending"
    | "eligible"
    | "protestRecommended"
    | "outOfWindow"
  >("overview");
  const [exportOpen, setExportOpen] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [
    regenerateWorkbook,
    { isLoading: isBuildingWorkbook },
  ] = useRegenerateIeepaAnalysisWorkbookMutation();

  const analytics = useCapeAnalytics(report);

  const asOfTag = report?.asOfDate ? fmtDate(report.asOfDate).replace(/\//g, ".") : "";

  // Build-on-download: the mutation re-aggregates from current Mongo data,
  // overwrites analysis.xlsx in S3, and returns a fresh signed URL — so the
  // workbook always matches what the page shows.
  const handleWorkbookDownload = async () => {
    if (!reportId || isBuildingWorkbook) return;
    setDownloadError(null);
    try {
      const res = await regenerateWorkbook({ reportId }).unwrap();
      if (!res?.data) {
        throw new Error("Workbook URL missing from server response.");
      }
      window.open(res.data, "_blank", "noopener,noreferrer");
    } catch (err: unknown) {
      const msg =
        (typeof err === "object" &&
          err !== null &&
          "error" in err &&
          typeof (err as { error: unknown }).error === "string"
          ? (err as { error: string }).error
          : typeof err === "object" &&
            err !== null &&
            "message" in err &&
            typeof (err as { message: unknown }).message === "string"
          ? (err as { message: string }).message
          : null) || "Workbook download failed.";
      setDownloadError(msg);
    }
  };

  const handleCapeReportDownload = (
    filtered: IeepaAnalysisEntry[],
    opts: ExportOptions
  ) => {
    const suffix = [
      opts.includeProtests ? "incl-protest" : null,
      opts.includeOutOfWindow ? "incl-out-of-window" : null,
      opts.includeRecon ? null : "excl-recon",
    ]
      .filter(Boolean)
      .join(", ");
    const filename = suffix
      ? `Cape Report ${asOfTag} (${suffix}).csv`
      : `Cape Report ${asOfTag}.csv`;
    triggerBlobDownload(
      new Blob([buildCapeReportCsv(filtered)], { type: "text/csv" }),
      filename
    );
  };

  if (!reportId) {
    return <Alert severity="error">Missing report id.</Alert>;
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !report) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Unable to load the analysis report.</Alert>
      </Box>
    );
  }

  return (
    <Stack sx={{ height: "100%", minHeight: 0, flex: 1, flexDirection: "column" }}>
      {/* ─── Header ─── */}
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.5, sm: 2, md: 3 },
          pt: { xs: 1.5, md: 2 },
          pb: 1.5,
          bgcolor: tokens.bg.muted,
          borderBottom: `1px solid ${tokens.border.default}`,
        }}
      >
        {showBackButton && (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              size="small"
              startIcon={<ArrowBack />}
              onClick={() => navigate("/tools/ieepa-refund-cape")}
              sx={{ color: tokens.text.muted }}
            >
              Back to Reports
            </Button>
          </Stack>
        )}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          gap={{ xs: 1.25, md: 1 }}
          sx={{ mt: showBackButton ? 1 : 0 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: 18, sm: 20, md: 24 },
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.primary,
                lineHeight: 1.2,
              }}
            >
              IEEPA Analysis
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted }}>
              As-of filing date {fmtDate(report.asOfDate)} · {report.totals.ieepaEntries} IEEPA entries
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: { xs: "wrap", md: "nowrap" },
              "& > .MuiButton-root": { flex: { xs: 1, md: "0 0 auto" }, minWidth: 0 },
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={
                isBuildingWorkbook ? (
                  <CircularProgress size={14} thickness={5} />
                ) : (
                  <InsertDriveFile />
                )
              }
              onClick={handleWorkbookDownload}
              disabled={isBuildingWorkbook || !reportId}
              sx={{ textTransform: "none", fontSize: tokens.fontSize.sm2, whiteSpace: "nowrap" }}
            >
              {isBuildingWorkbook ? "Building…" : "Full Workbook"}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<Download />}
              onClick={() => setExportOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: tokens.fontSize.sm2,
                whiteSpace: "nowrap",
                bgcolor: tokens.brand.primary,
                "&:hover": { bgcolor: tokens.brand.primaryHover },
              }}
            >
              Cape Report
            </Button>
          </Stack>
        </Stack>
      </Box>

      <ExportCapeReportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        entries={report.entries}
        onDownload={handleCapeReportDownload}
      />

      <Snackbar
        open={!!downloadError}
        autoHideDuration={6000}
        onClose={() => setDownloadError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="error"
          onClose={() => setDownloadError(null)}
          sx={{ width: "100%" }}
        >
          {downloadError}
        </Alert>
      </Snackbar>

      {/* ─── Tabs ─── */}
      <Box
        sx={{
          borderBottom: `1px solid ${tokens.border.default}`,
          px: { xs: 1, sm: 2, md: 3 },
          mt: 1,
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            maxWidth: "100%",
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: tokens.fontSize.md,
              fontWeight: tokens.fontWeight.semibold,
              minHeight: 40,
              whiteSpace: "nowrap",
              "&.Mui-selected": { color: tokens.brand.primary },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: tokens.brand.primary,
              height: 2,
            },
            "& .MuiTabs-scrollButtons.Mui-disabled": {
              opacity: 0.3,
            },
          }}
        >
          <Tab value="overview" label="Overview" />
          <Tab
            value="certified"
            label={`CAPE Certified (${analytics.capeCertifiedEntries.length})`}
          />
          <Tab
            value="pending"
            label={`CAPE Pending (${analytics.capePendingEntries.length})`}
          />
          <Tab
            value="eligible"
            label={`CAPE Eligible (${analytics.capeEligibleOnlyEntries.length})`}
          />
          <Tab
            value="protestRecommended"
            label={`Protest Recommended (${analytics.protestRecommendedEntries.length})`}
          />
          <Tab
            value="outOfWindow"
            label={`Out of Window (${analytics.outOfWindowEntries.length})`}
          />
        </Tabs>
      </Box>

      {/* ─── Body ─── */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          p: { xs: 1.5, sm: 2, md: 3 },
        }}
      >
        {activeTab === "overview" && (
          <FunnelOverview report={report} analytics={analytics} />
        )}

        {activeTab === "certified" && (
          <EntryTable
            variant="certified"
            rows={analytics.capeCertifiedEntries}
            emptyMessage="No CAPE-certified refunds yet for this client."
          />
        )}

        {activeTab === "pending" && (
          <EntryTable
            variant="pending"
            rows={analytics.capePendingEntries}
            emptyMessage="No CAPE-pending entries — none have a measurable paid − line refund."
          />
        )}

        {activeTab === "eligible" && (
          <EntryTable
            rows={analytics.capeEligibleOnlyEntries}
            emptyMessage="No CAPE-eligible entries without a filed claim."
          />
        )}

        {activeTab === "protestRecommended" && (
          <EntryTable
            rows={analytics.protestRecommendedEntries}
            emptyMessage="No entries in the 81–180 day protest window."
          />
        )}

        {activeTab === "outOfWindow" && (
          <EntryTable
            rows={analytics.outOfWindowEntries}
            emptyMessage="No entries past the 180-day protest window."
          />
        )}
      </Box>
    </Stack>
  );
};

export default IeepaRefundCapeDetail;
